// Check if RELEASE_ENV is explicitly set to 'true' (case-insensitive)
// If RELEASE_ENV is 'true' → use production config
// If RELEASE_ENV is undefined, null, false, or any other value → use development config
// Use NEXT_PUBLIC_RELEASE_ENV for client-side access, fallback to RELEASE_ENV for server-side
export const releaseEnv =
  process.env.NEXT_PUBLIC_RELEASE_ENV || process.env.RELEASE_ENV;
export const isProduction = releaseEnv?.toLowerCase() === 'true';
export const isStaging = releaseEnv?.toLowerCase() === 'staging';

// Explicit 'development' | 'staging' | 'production' identifier, set at
// build time via the NEXT_PUBLIC_APP_ENV build arg (see gcp-deploy.yml).
export const appEnv = process.env.NEXT_PUBLIC_APP_ENV || process.env.APP_ENV;
const predictorFlagEnabled =
  process.env.NEXT_PUBLIC_PREDICTOR_ENABLED?.toLowerCase() === 'true';
export const isPredictorEnabled =
  predictorFlagEnabled ||
  (!isProduction &&
    (appEnv?.toLowerCase() === 'development' ||
      appEnv?.toLowerCase() === 'staging' ||
      isStaging));

export const isLocalEnv =
  String(
    process.env.NEXT_PUBLIC_IS_LOCAL || process.env.IS_LOCAL
  ).toLowerCase() === 'true';

// Common configuration values (shared across environments)
const commonConfig = {
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: 'AIzaSyBRaPDpk_nksgaibBA_D8Uz0JJZBT5BnCY',
  NEXT_PUBLIC_API_CMS_URL: 'https://api.scholarbee.pk/api',
  NEXT_PUBLIC_S3_URL: 'https://scolarbee-s3-bucket.s3.us-east-1.amazonaws.com/',
  NEXT_PUBLIC_GIT_SHA: '',
  NEXT_PUBLIC_GIT_BRANCH: '',
  NEXT_PUBLIC_GIT_REPO: ''
} as const;

// Environment-specific configuration values
const environmentConfig = {
  local: {
    NEXT_PUBLIC_API_BASE_URL: 'http://localhost:3010/api',
    NEXT_PUBLIC_CHAT_SOCKET_URL: 'ws://localhost:3010/chat',
    NEXT_PUBLIC_NOTIFICATION_SOCKET_URL: 'ws://localhost:3010/notifications'
  },
  development: {
    NEXT_PUBLIC_API_BASE_URL: 'https://api-dev.scholarbee.pk/api',
    NEXT_PUBLIC_CHAT_SOCKET_URL: 'wss://ws.api-dev.scholarbee.pk/chat',
    NEXT_PUBLIC_NOTIFICATION_SOCKET_URL:
      'wss://ws.api-dev.scholarbee.pk/notifications'
  },
  staging: {
    NEXT_PUBLIC_API_BASE_URL: 'https://api-staging.scholarbee.pk/api',
    NEXT_PUBLIC_CHAT_SOCKET_URL: 'wss://ws.api-staging.scholarbee.pk/chat',
    NEXT_PUBLIC_NOTIFICATION_SOCKET_URL:
      'wss://ws.api-staging.scholarbee.pk/notifications'
  },
  production: {
    NEXT_PUBLIC_API_BASE_URL: 'https://api-prod.scholarbee.pk/api',
    NEXT_PUBLIC_CHAT_SOCKET_URL: 'wss://ws.api-prod.scholarbee.pk/chat',
    NEXT_PUBLIC_NOTIFICATION_SOCKET_URL:
      'wss://ws.api-prod.scholarbee.pk/notifications'
  }
} as const;

// Type for config keys
type ConfigKey =
  | keyof typeof commonConfig
  | keyof typeof environmentConfig.development;

// Get config value: prioritize env var, then fallback to environment-specific config
const getConfig = (key: ConfigKey): string => {
  const envVar = process.env[key];
  if (envVar) return envVar;

  // Check common config first
  if (key in commonConfig) {
    return commonConfig[key as keyof typeof commonConfig];
  }

  // Then check environment-specific config
  const env = isProduction
    ? 'production'
    : isStaging
      ? 'staging'
      : isLocalEnv
        ? 'local'
        : 'development';
  return environmentConfig[env][
    key as keyof typeof environmentConfig.development
  ];
};

// Export configuration values
export const config = {
  GOOGLE_MAPS_API_KEY: getConfig('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY'),
  API_CMS_URL: getConfig('NEXT_PUBLIC_API_CMS_URL'),
  API_BASE_URL: getConfig('NEXT_PUBLIC_API_BASE_URL'),
  API_BASE_URL_DEV: (() => {
    // Use environment-specific config based on RELEASE_ENV
    // RELEASE_ENV=true → production (api-prod)
    // RELEASE_ENV=staging → staging (api-staging)
    // RELEASE_ENV undefined/null/false/any other value → development (api-dev)
    return isProduction
      ? environmentConfig.production.NEXT_PUBLIC_API_BASE_URL
      : isStaging
        ? environmentConfig.staging.NEXT_PUBLIC_API_BASE_URL
        : isLocalEnv
          ? environmentConfig.local.NEXT_PUBLIC_API_BASE_URL
          : environmentConfig.development.NEXT_PUBLIC_API_BASE_URL;
  })(),
  CHAT_SOCKET_URL: getConfig('NEXT_PUBLIC_CHAT_SOCKET_URL'),
  NOTIFICATION_SOCKET_URL: getConfig('NEXT_PUBLIC_NOTIFICATION_SOCKET_URL'),
  S3_URL: getConfig('NEXT_PUBLIC_S3_URL'),
  GIT_SHA: process.env.NEXT_PUBLIC_GIT_SHA || '',
  GIT_BRANCH: process.env.NEXT_PUBLIC_GIT_BRANCH || '',
  GIT_REPO: process.env.NEXT_PUBLIC_GIT_REPO || ''
};

// Export individual values for convenience
export const GOOGLE_MAPS_API_KEY = config.GOOGLE_MAPS_API_KEY;
export const API_CMS_URL = config.API_CMS_URL;
export const API_BASE_URL = config.API_BASE_URL;
export const API_BASE_URL_DEV = config.API_BASE_URL_DEV;

// Backend URL for server-side fetches (serverData.ts, route handlers) only.
// INTERNAL_API_BASE_URL points at the backend's Cloud Run URL so SSR calls
// stay inside Google's network instead of going out through Cloudflare.
// It has no NEXT_PUBLIC_ prefix, so it is read at runtime and is never
// inlined into the browser bundle. When unset, the public URL is used.
export const SERVER_API_BASE_URL =
  process.env.INTERNAL_API_BASE_URL || API_BASE_URL_DEV;
export const CHAT_SOCKET_URL = config.CHAT_SOCKET_URL;
export const NOTIFICATION_SOCKET_URL = config.NOTIFICATION_SOCKET_URL;
export const S3_URL = config.S3_URL;
export const gitSha = process.env.NEXT_PUBLIC_GIT_SHA || '';
export const gitBranch = process.env.NEXT_PUBLIC_GIT_BRANCH || '';
export const gitRepo = process.env.NEXT_PUBLIC_GIT_REPO || '';
